import { useState, useEffect, useCallback, useMemo, ReactNode } from 'react'
import { AuthContext } from './AuthProvider'
import { storage, TOKEN_KEY, USER_ID_KEY } from '../utils/storage'
import { identifyUser, resetUser, events } from '../lib/posthog'
import { apiClient } from '../api/client'
import type { AuthUser } from '../types'

/**
 * Development Authentication Provider
 *
 * Mocks MitID authentication for local development.
 * Only active when __DEV__ is true.
 * Uses SecureStore/localStorage token (same key as production) to persist auth across app restarts.
 *
 * Matches Next.js DevAuthProvider behavior exactly:
 * - onboarding_completed defaults to false (show onboarding so testers see full flow)
 * - Session-scoped "demo_onboarding_seen" flag (module-level var = closest to sessionStorage)
 * - PostHog events tracked on login/logout
 */

const DEV_USER: AuthUser = {
  id: 'dev-user-123',
  email: 'gustav@forbrugeragenten.dk',
  full_name: 'Gustav Louv',
  cpr_number: '0101901234',
  mitid_verified: true,
  onboarding_completed: true, // Use DB value via API — seed sets this to true
  fuldmagt_accepted: true,
  consent_given: true,
}

const DEV_TOKEN = 'dev-mock-token-12345'

// Use storage for 'demo_onboarding_seen' — equivalent to sessionStorage in Next.js.
// On web, storage uses localStorage (persists across page navigations within same session).
// On native, storage uses SecureStore (persists until app restart).
// Module-level cache to avoid async read in synchronous contexts:
let demoOnboardingSeen = false

// Initialize from storage (async — called on provider mount)
async function initDemoOnboardingSeen() {
  const val = await storage.get('demo_onboarding_seen')
  demoOnboardingSeen = !!val
}

export async function setDemoOnboardingSeen(seen: boolean) {
  demoOnboardingSeen = seen
  if (seen) {
    await storage.set('demo_onboarding_seen', '1')
  } else {
    await storage.remove('demo_onboarding_seen')
  }
}

export function DevAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(true)

  // On mount: init demoOnboardingSeen from storage, then check token + fetch API data
  useEffect(() => {
    ;(async () => {
      await initDemoOnboardingSeen()
      const token = await storage.get(TOKEN_KEY)
      if (token) {
        await storage.set(USER_ID_KEY, DEV_USER.id)
        // Fetch real user data from API to get beta_preference, current_providers, etc.
        try {
          const res = await apiClient.auth.me(DEV_TOKEN)
          const me = res.data
          setUser({
            ...DEV_USER,
            full_name: me.full_name ?? DEV_USER.full_name,
            onboarding_completed: me.onboarding_completed ?? demoOnboardingSeen,
            beta_preference: me.beta_preference ?? undefined,
            current_providers: me.current_providers ?? undefined,
            created_at: me.created_at ?? undefined,
            last_nps_at: me.last_nps_at ?? undefined,
          })
        } catch {
          setUser({ ...DEV_USER, onboarding_completed: demoOnboardingSeen })
        }
      }
      setIsLoading(false)
    })()
  }, [])

  const loginWithRedirect = useCallback(async () => {
    console.log('[DEV] Mock MitID login')
    events.loginStarted()
    await storage.set(TOKEN_KEY, DEV_TOKEN)
    await storage.set(USER_ID_KEY, DEV_USER.id)
    // Fetch real user data from API (onboarding_completed, full_name, etc.)
    try {
      const res = await apiClient.auth.me(DEV_TOKEN)
      const me = res.data
      setUser({
        ...DEV_USER,
        full_name: me.full_name ?? DEV_USER.full_name,
        onboarding_completed: me.onboarding_completed ?? false,
        beta_preference: me.beta_preference ?? undefined,
        current_providers: me.current_providers ?? undefined,
        created_at: me.created_at ?? undefined,
      })
    } catch {
      setUser({ ...DEV_USER, onboarding_completed: false })
    }
    identifyUser(DEV_USER.id, { email: DEV_USER.email, name: DEV_USER.full_name })
    events.loginCompleted('dev')
  }, [])

  const logout = useCallback(async () => {
    console.log('[DEV] Logout')
    events.logout()
    resetUser()
    await storage.remove(TOKEN_KEY)
    await storage.remove(USER_ID_KEY)
    // Also remove legacy 'auth_token' key — matches Next.js DevAuthProvider
    await storage.remove('auth_token')
    setUser(undefined)
    // Navigation handled by auth layout redirect (Expo equivalent of window.location.href = '/login')
  }, [])

  const getAccessTokenSilently = useCallback(async () => {
    return DEV_TOKEN
  }, [])

  const refreshUser = useCallback(async () => {
    // Fetch latest user data from API (gets beta_preference, current_providers, etc.)
    try {
      const res = await apiClient.auth.me(DEV_TOKEN)
      const me = res.data
      setUser({
        ...DEV_USER,
        full_name: me.full_name ?? DEV_USER.full_name,
        onboarding_completed: me.onboarding_completed ?? demoOnboardingSeen,
        beta_preference: me.beta_preference ?? undefined,
        current_providers: me.current_providers ?? undefined,
        created_at: me.created_at ?? undefined,
        last_nps_at: me.last_nps_at ?? undefined,
      })
    } catch {
      // Fallback to local state if API fails
      const token = await storage.get(TOKEN_KEY)
      if (token) {
        setUser({ ...DEV_USER, onboarding_completed: demoOnboardingSeen })
      }
    }
  }, [])

  const setUserDirectly = useCallback((u: AuthUser) => {
    setUser(u)
  }, [])

  const value = useMemo(
    () => ({
      user,
      isAuthenticated: !!user,
      isLoading,
      loginWithRedirect,
      logout,
      getAccessTokenSilently,
      refreshUser,
      setUserDirectly,
    }),
    [user, isLoading, loginWithRedirect, logout, getAccessTokenSilently, refreshUser, setUserDirectly]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
